/**
 * PREMIUM COLOR ENGINE
 * Better contrast + stable hover/active states
 */

/* =========================
   HEX -> RGB
========================= */
function hexToRgb(hex) {
  if (!hex) return null;

  let cleanHex = hex.replace("#", "");

  // support #fff
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  if (cleanHex.length !== 6) return null;

  const bigint = parseInt(cleanHex, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

/* =========================
   RGB -> HEX
========================= */
function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

/* =========================
   BRIGHTNESS
========================= */
export function getColorBrightness(hexColor) {
  const rgb = hexToRgb(hexColor);

  if (!rgb) return 0.5;

  return (
    (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) /
    1000 /
    255
  );
}

/* =========================
   TEXT COLOR
========================= */
export function getTextColor(bgColor) {
  const brightness = getColorBrightness(bgColor);
  return brightness > 0.72 ? "#111827" : "#FFFFFF";
}

/* =========================
   MIX COLORS
========================= */
export function mixColors(color1, color2, amount) {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  if (!c1 || !c2) return color1;

  const mix = (a, b) =>
    Math.round(a + (b - a) * amount);

  return rgbToHex(
    mix(c1.r, c2.r),
    mix(c1.g, c2.g),
    mix(c1.b, c2.b)
  );
}

/* =========================
   HOVER COLOR
========================= */
export function getHoverColor(bgColor) {
  const brightness = getColorBrightness(bgColor);

  // dark bg -> slightly lighter
  if (brightness < 0.5) {
    return mixColors(bgColor, "#ffffff", 0.12);
  }

  // light bg -> slightly darker
  return mixColors(bgColor, "#000000", 0.08);
}

/* =========================
   ACTIVE COLOR
========================= */
export function getActiveColor(bgColor) {
  const brightness = getColorBrightness(bgColor);

  // Premium glassy active state
  if (brightness < 0.5) {
    return "rgba(255,255,255,0.16)";
  }

  return "rgba(0,0,0,0.08)";
}

/* =========================
   BORDER COLOR
========================= */
export function getBorderColor(bgColor) {
  const brightness = getColorBrightness(bgColor);

  return brightness < 0.5
    ? "rgba(255,255,255,0.12)"
    : "rgba(0,0,0,0.08)";
}

/* =========================
   OVERLAY COLOR
========================= */
export function getOverlayColor(bgColor, alpha = 0.1) {
  const rgb = hexToRgb(bgColor);

  if (!rgb) return `rgba(255,255,255,${alpha})`;

  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

/* =========================
   PREMIUM ACCENT
========================= */
export function getAccentColor(bgColor) {
  const brightness = getColorBrightness(bgColor);

  // instead of ugly complementary colors
  // use elegant tint

  if (brightness < 0.5) {
    return mixColors(bgColor, "#ffffff", 0.35);
  }

  return mixColors(bgColor, "#000000", 0.25);
}
export const darkenColor = (hex, percent = 10) => {
  hex = hex.replace("#", "");

  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  r = Math.floor(r * (100 - percent) / 100);
  g = Math.floor(g * (100 - percent) / 100);
  b = Math.floor(b * (100 - percent) / 100);

  return `rgb(${r}, ${g}, ${b})`;
};